<script>
import {ref} from "vue";
import {IMaskDirective} from 'vue-imask';
import IconX from "@js/components/Icons/IconX.vue";
import CallbackErrorMessage from "@js/components/Statuses/CallbackErrorMessage.vue";
import SpinnerIndicator from "@js/components/Statuses/SpinnerIndicator.vue";
import PrivacyPolicyAgreementLink from "@js/components/Sections/Links/PrivacyPolicyAgreementLink.vue";
import OnlyDesktop from "@js/components/DynamicComponents/OnlyDesktop.vue";
import OnlyMobile from "@js/components/DynamicComponents/OnlyMobile.vue";
import IconAnimatedCheckMark from "@js/components/Icons/IconAnimatedCheckMark.vue";
import ImageLoader from "@js/components/Loaders/ImageLoader.vue";
import {useProductStore} from "@js/store/ProductStore.js";
import NumberFormatComponent from "@js/components/Formatter/NumberFormatComponent.vue";

export default {
    name: "FastBuyCallback",
    components: {
        NumberFormatComponent,
        ImageLoader,
        IconAnimatedCheckMark,
        OnlyMobile,
        OnlyDesktop,
        PrivacyPolicyAgreementLink,
        SpinnerIndicator,
        CallbackErrorMessage,
        IconX,
    },
    directives: {
        imask: IMaskDirective
    },
    data() {
        return {
            phoneMask: {
                mask: '+7 (000) 000-00-00',
            },
        }
    },
    methods: {
        updatePhone(newPhone) {
            this.productStore.callback.data.phone = newPhone;
        },
    },
    setup() {
        const productStore = useProductStore()
        const modalRef = ref(null)

        const closeModal = () => {
            productStore.closeFastBuyPopup()
        }

        const handleBackgroundClick = (event) => {
            if (!modalRef.value.contains(event.target)) {
                closeModal()
            }
        }

        // Обработка свайпа вниз
        const touchStartY = ref(0);
        const touchDeltaY = ref(0);
        const onTouchStart = (e) => {
            touchStartY.value = e.touches[0].clientY;
        };
        const onTouchMove = (e) => {
            touchDeltaY.value = e.touches[0].clientY - touchStartY.value;
            if (touchDeltaY.value > 0 && modalRef.value) {
                modalRef.value.style.transform = `translateY(${touchDeltaY.value}px)`;
            }
        };
        const onTouchEnd = () => {
            if (touchDeltaY.value > 100) {
                closeModal();
            } else if (modalRef.value) {
                // Вернуть в исходное положение
                modalRef.value.style.transition = "transform 0.3s";
                modalRef.value.style.transform = "translateY(0)";
                setTimeout(() => {
                    if (modalRef.value) {
                        modalRef.value.style.transition = "";
                    }
                }, 1000);
            }
            touchDeltaY.value = 0;
        };

        return {
            productStore,
            modalRef,
            closeModal,
            handleBackgroundClick,
            onTouchStart,
            onTouchMove,
            onTouchEnd,
        }
    },
}
</script>

<template>
    <div v-if="productStore.callback.isOpen"
         class="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black bg-opacity-70 transition-opacity "
         @click="handleBackgroundClick">
        <div ref="modalRef"
             @touchstart.passive="onTouchStart"
             @touchmove.passive="onTouchMove"
             @touchend="onTouchEnd"
             :class="{'top-i-0': productStore.callback.isOpen && productStore.callback.animation_in_progress}"
             class="relative top-[100%] bg-white bg-contain bg-no-repeat bg-center rounded-tr-2xl rounded-tl-2xl sm:rounded-2xl shadow-lg max-h-[550px] sm:max-w-[450px] sm:max-h-[500px] w-full h-full transition-all duration-500 overflow-hidden p-5">
            <button @click.prevent="closeModal"
                    class="absolute top-2 right-2 text-gray-400 md:hover:text-white focus:outline-none">
                <IconX
                    class="h-7 w-7 stroke-[#000] active:stroke-baseColor sm:hover:stroke-baseColor transition"/>
            </button>
            <OnlyMobile :maxSize="1024" class="absolute top-3 right-0 left-0 m-auto flex justify-center">
                <div class="h-[5px] w-[50px] bg-gray-400 rounded-full"></div>
            </OnlyMobile>
            <div
                class="w-full h-full flex flex-col items-start sm:items-center max-sm:mt-5">
                <div class="w-full max-w-sm mx-auto mb-5">
                    <div class="text-xl font-semibold text-gray-700 mb-5">
                        Заявка на покупку товара
                    </div>
                    <div class="grid grid-cols-[100px_1fr] text-left gap-5">
                        <div class="w-[100px] h-[100px] p-1 border border-grayBg rounded-xl overflow-hidden">
                            <ImageLoader :title="productStore.callback.product.title"
                                         :src="'/storage/assets/products/'+productStore.callback.product.category.slug+'/preview/'+ productStore.callback.product.media.images.preview_square"
                                         imgClasses="w-full h-full object-contain "/>
                        </div>
                        <div>
                            <p class="text-base text-[#2D2D2D] font-medium">
                                {{ productStore.callback.product.title }}
                            </p>
                            <p v-if="productStore.callback.product.price_per_meter"
                               class="font-bold">
                                <NumberFormatComponent :number="productStore.callback.product.price_per_meter"
                                                       append_text=" ₽"
                                                       prepend_text="Цена за метр: "
                                                       class="text-sm text-gray-600"/>
                            </p>
                            <p v-if="productStore.callback.product.price_per_ton"
                               class="font-bold">
                                <NumberFormatComponent :number="productStore.callback.product.price_per_ton"
                                                       append_text=" ₽"
                                                       prepend_text="Цена за тонну: "
                                                       class="text-sm text-gray-600"/>
                            </p>
                            <p v-if="productStore.callback.product.price_per_piece"
                               class="font-bold">
                                <NumberFormatComponent :number="productStore.callback.product.price_per_piece"
                                                       append_text=" ₽"
                                                       prepend_text="Цена за штуку: "
                                                       class="text-sm text-gray-600"/>
                            </p>
                            <p v-if="!productStore.callback.product.price_per_meter && !productStore.callback.product.price_per_ton && !productStore.callback.product.price_per_piece"
                               class="font-bold text-sm text-gray-600">
                                Цена: договорная
                            </p>
                        </div>
                    </div>
                </div>
                <div v-if="!productStore.callback.successfully_sent"
                     class="w-full max-w-sm mx-auto">
                    <div class="text-gray-700 font-semibold mb-3">
                        Данные покупателя
                    </div>
                    <div class="mb-5">
                        <div class="relative">
                            <input type="text"
                                   id="ptf_phone"
                                   @keyup="updatePhone($event.target.value)"
                                   :value="productStore.callback.data.phone"
                                   v-imask="phoneMask"
                                   class="block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-lg border-1 border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-baseColor peer"
                                   :class="[productStore.callback.errors && productStore.callback.errors['phone'] ? 'border-red-500' : '']"
                                   placeholder=" " required/>
                            <label for="ptf_phone"
                                   class="absolute text-sm text-gray-500 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-baseColor peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1">
                                Номер телефона
                            </label>
                        </div>
                        <CallbackErrorMessage :error="productStore.callback.errors"
                                              :error-msg="'phone'"/>
                    </div>
                    <div class="mb-5">
                        <div class="relative">
                            <input type="text"
                                   id="ptf_full_name"
                                   v-model="productStore.callback.data.full_name"
                                   class="block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-lg border-1 border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-baseColor peer"
                                   :class="[productStore.callback.errors && productStore.callback.errors['full_name'] ? 'border-red-500' : '']"
                                   placeholder=" " required/>
                            <label for="ptf_full_name"
                                   class="absolute text-sm text-gray-500 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-baseColor peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1">
                                Как к вам обращаться?
                            </label>
                        </div>
                        <CallbackErrorMessage :error="productStore.callback.errors"
                                              :error-msg="'full_name'"/>
                    </div>
                    <div class="relative w-full lg:w-auto flex justify-center">
                        <button @click.stop="productStore.sendPopupForm"
                                :disabled="productStore.callback.sending"
                                class="flex justify-center items-center w-full text-white md:hover:text-baseColor border-2 border-baseColor bg-baseColor md:hover:bg-transparent font-medium rounded-lg text-sm px-14 py-2.5 focus:outline-none transition cursor-pointer">
                            <span v-if="!productStore.callback.sending" class="text-base">
                                Отправить заказ
                            </span>
                            <span v-else class="text-base">
                                Отправляем
                            </span>
                            <SpinnerIndicator v-if="productStore.callback.sending"
                                              class="w-6 h-6 ms-2"/>
                        </button>
                    </div>
                    <div class="mt-5">
                        <PrivacyPolicyAgreementLink/>
                    </div>
                </div>
                <div v-else class="w-full max-w-sm mx-auto p-3 sm:p-5 flex flex-col items-center">
                    <IconAnimatedCheckMark size="80"/>
                    <p class="text-lg font-medium text-center text-[#7ac142] mt-5">
                        Спасибо!<br/> Мы скоро свяжемся с вами.
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">

</style>
